import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ActionCodeOperation,
  applyActionCode,
  checkActionCode,
  type ActionCodeInfo,
} from 'firebase/auth';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import AuthActionShell from '../components/Auth/AuthActionShell';
import { Alert, Button, Spinner } from '../components/common';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase';
import { authService } from '../services/authService';
import { syncEmailVerificationStatus } from '../services/emailVerification';
import { resolveDefaultAppLanding, resolvePostAuthRedirect } from '../utils/authRedirect';
import {
  consumePostAuthIntent,
  encodePostAuthIntentForQuery,
  getPostAuthIntent,
  resolvePostAuthIntentFromContinueUrl,
  savePostAuthIntent,
} from '../utils/postAuthIntent';
import ResetPasswordAction from './ResetPasswordAction';

type ParsedAuthAction =
  | {
      kind: 'resetPassword';
      mode: 'resetPassword';
      oobCode: string;
      continueIntent: string | null;
      lang: string | null;
    }
  | {
      kind: 'verifyEmail';
      mode: 'verifyEmail';
      oobCode: string;
      continueIntent: string | null;
      lang: string | null;
    }
  | {
      kind: 'malformed';
      reason: 'missing-mode' | 'missing-code';
      mode: string | null;
      lang: string | null;
    }
  | {
      kind: 'unsupported';
      mode: string;
      lang: string | null;
    };

type VerifyEmailState =
  | { phase: 'parsing' }
  | { phase: 'checking' }
  | { phase: 'ready'; targetEmail: string; maskedEmail: string }
  | { phase: 'applying'; targetEmail: string; maskedEmail: string }
  | { phase: 'synchronizing'; targetEmail: string; maskedEmail: string }
  | {
      phase: 'success';
      accountContext: 'matching' | 'signed-out' | 'different';
      targetEmail: string;
      maskedEmail: string;
      nextPath?: string;
      profileSync: 'confirmed' | 'unconfirmed' | 'not-applicable';
      warning?: string;
    }
  | {
      phase: 'recoverable-error';
      message: string;
      targetEmail?: string;
      maskedEmail?: string;
    }
  | {
      phase: 'terminal-error';
      message: string;
      targetEmail?: string;
      maskedEmail?: string;
    };

interface VerifyEmailActionProps {
  oobCode: string;
  continueIntent: string | null;
}

interface VerifyErrorState {
  terminal: boolean;
  message: string;
}

const normalizeEmail = (email: unknown): string =>
  String(email || '')
    .trim()
    .toLowerCase();

const maskEmail = (email: string): string => {
  const normalizedEmail = String(email || '').trim();
  const [localPart = '', domain = ''] = normalizedEmail.split('@');
  if (!localPart || !domain) {
    return 'ukryty adres';
  }

  const maskSegment = (segment: string): string => {
    if (segment.length <= 1) {
      return `${segment.slice(0, 1)}***`;
    }
    return `${segment.slice(0, 1)}***${segment.slice(-1)}`;
  };

  const domainParts = domain.split('.');
  const domainName = domainParts.shift() || '';
  const suffix = domainParts.length > 0 ? `.${domainParts.join('.')}` : '';
  return `${maskSegment(localPart)}@${maskSegment(domainName)}${suffix}`;
};

const mapVerifyActionError = (errorCode: string): VerifyErrorState => {
  switch (errorCode) {
    case 'auth/expired-action-code':
      return {
        terminal: true,
        message: 'Ten link weryfikacyjny wygasł. Poproś o wysłanie nowego linku.',
      };
    case 'auth/invalid-action-code':
      return {
        terminal: true,
        message: 'Ten link jest nieprawidłowy, wygasł albo został już użyty.',
      };
    case 'auth/user-disabled':
      return {
        terminal: true,
        message: 'Konto powiązane z tym linkiem jest wyłączone.',
      };
    case 'auth/user-not-found':
      return {
        terminal: true,
        message: 'Nie można odnaleźć konta powiązanego z tym linkiem.',
      };
    case 'auth/network-request-failed':
      return {
        terminal: false,
        message: 'Nie udało się połączyć z Firebase. Sprawdź internet i spróbuj ponownie.',
      };
    case 'auth/too-many-requests':
      return {
        terminal: false,
        message: 'Wykonano zbyt wiele prób. Odczekaj chwilę i spróbuj ponownie.',
      };
    default:
      return {
        terminal: false,
        message: 'Nie udało się sprawdzić linku. Spróbuj ponownie.',
      };
  }
};

const getActionCodeEmail = (actionCodeInfo: ActionCodeInfo): string | null => {
  const email = String(actionCodeInfo.data.email || '').trim();
  return email || null;
};

const parseAuthActionQuery = (searchParams: URLSearchParams): ParsedAuthAction => {
  const rawMode = String(searchParams.get('mode') || '').trim();
  const oobCode = String(searchParams.get('oobCode') || '').trim();
  const continueUrl = String(searchParams.get('continueUrl') || '').trim();
  const lang = String(searchParams.get('lang') || '').trim() || null;
  const continueIntent = resolvePostAuthIntentFromContinueUrl(continueUrl);

  if (!rawMode) {
    return { kind: 'malformed', reason: 'missing-mode', mode: null, lang };
  }
  if (rawMode !== 'resetPassword' && rawMode !== 'verifyEmail') {
    return { kind: 'unsupported', mode: rawMode, lang };
  }
  if (!oobCode) {
    return { kind: 'malformed', reason: 'missing-code', mode: rawMode, lang };
  }
  if (rawMode === 'resetPassword') {
    return { kind: 'resetPassword', mode: rawMode, oobCode, continueIntent, lang };
  }
  return { kind: 'verifyEmail', mode: rawMode, oobCode, continueIntent, lang };
};

export const removeSensitiveAuthActionParams = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);
  ['oobCode', 'apiKey', 'continueUrl', 'postAuthIntent'].forEach((key) =>
    url.searchParams.delete(key)
  );
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};

const buildLoginTarget = (intentPath: string | null): string => {
  const encodedIntent = encodePostAuthIntentForQuery(intentPath);
  return encodedIntent ? `/login?postAuthIntent=${encodedIntent}` : '/login';
};

const VerifyEmailAction: React.FC<VerifyEmailActionProps> = ({ oobCode, continueIntent }) => {
  const navigate = useNavigate();
  const { userProfile, claims } = useAuth();
  const [state, setState] = useState<VerifyEmailState>({ phase: 'parsing' });
  const [isLeaving, setIsLeaving] = useState(false);
  const submitLockRef = useRef(false);
  const stateHeadingRef = useRef<HTMLHeadingElement>(null);

  const resolveIntentPath = useCallback((): string | null => {
    return continueIntent || getPostAuthIntent();
  }, [continueIntent]);

  const checkCode = useCallback(async () => {
    setState({ phase: 'checking' });
    try {
      const actionCodeInfo = await checkActionCode(auth, oobCode);
      if (actionCodeInfo.operation !== ActionCodeOperation.VERIFY_EMAIL) {
        removeSensitiveAuthActionParams();
        setState({
          phase: 'terminal-error',
          message: 'Ten link nie służy do potwierdzenia adresu e-mail.',
        });
        return;
      }

      const targetEmail = getActionCodeEmail(actionCodeInfo);
      if (!targetEmail) {
        removeSensitiveAuthActionParams();
        setState({
          phase: 'terminal-error',
          message: 'Firebase nie zwrócił adresu powiązanego z tym linkiem.',
        });
        return;
      }

      setState({ phase: 'ready', targetEmail, maskedEmail: maskEmail(targetEmail) });
    } catch (error: any) {
      const mappedError = mapVerifyActionError(String(error?.code || 'unknown'));
      if (mappedError.terminal) {
        removeSensitiveAuthActionParams();
      }
      setState({
        phase: mappedError.terminal ? 'terminal-error' : 'recoverable-error',
        message: mappedError.message,
      });
    }
  }, [oobCode]);

  useEffect(() => {
    if (state.phase === 'parsing') {
      void checkCode();
    }
  }, [checkCode, state.phase]);

  useEffect(() => {
    if (
      state.phase === 'ready' ||
      state.phase === 'success' ||
      state.phase === 'recoverable-error' ||
      state.phase === 'terminal-error'
    ) {
      stateHeadingRef.current?.focus();
    }
  }, [state.phase]);

  const setPartialSuccess = useCallback((targetEmail: string, maskedEmail: string) => {
    setState({
      phase: 'success',
      accountContext: 'matching',
      targetEmail,
      maskedEmail,
      profileSync: 'unconfirmed',
      warning:
        'Adres został potwierdzony w Firebase, ale status konta w aplikacji nie został jeszcze potwierdzony.',
    });
  }, []);

  const handleApply = async () => {
    if (state.phase !== 'ready' || submitLockRef.current) {
      return;
    }

    submitLockRef.current = true;
    const { targetEmail, maskedEmail } = state;
    setState({ phase: 'applying', targetEmail, maskedEmail });

    try {
      await applyActionCode(auth, oobCode);
      removeSensitiveAuthActionParams();

      const signedInUser = auth.currentUser;
      if (!signedInUser) {
        setState({
          phase: 'success',
          accountContext: 'signed-out',
          targetEmail,
          maskedEmail,
          profileSync: 'unconfirmed',
          warning:
            'Adres jest potwierdzony w Firebase. Status konta w aplikacji zostanie uzgodniony po zalogowaniu.',
        });
        return;
      }

      if (normalizeEmail(signedInUser.email) !== normalizeEmail(targetEmail)) {
        setState({
          phase: 'success',
          accountContext: 'different',
          targetEmail,
          maskedEmail,
          profileSync: 'not-applicable',
          warning:
            'Adres został potwierdzony, ale w tej przeglądarce jest teraz zalogowane inne konto.',
        });
        return;
      }

      setState({ phase: 'synchronizing', targetEmail, maskedEmail });
      try {
        await signedInUser.reload();
        if (signedInUser.emailVerified !== true) {
          setPartialSuccess(targetEmail, maskedEmail);
          return;
        }
        await signedInUser.getIdToken(true);
        const syncResult = await syncEmailVerificationStatus();
        if (syncResult.success !== true || syncResult.profileStatus !== 'active') {
          setPartialSuccess(targetEmail, maskedEmail);
          return;
        }

        const fallback = resolveDefaultAppLanding(userProfile, claims);
        const nextPath = resolvePostAuthRedirect({
          from: null,
          intentPath: resolveIntentPath(),
          fallback,
        });
        setState({
          phase: 'success',
          accountContext: 'matching',
          targetEmail,
          maskedEmail,
          nextPath,
          profileSync: 'confirmed',
        });
      } catch (_readbackError) {
        setPartialSuccess(targetEmail, maskedEmail);
      }
    } catch (error: any) {
      const mappedError = mapVerifyActionError(String(error?.code || 'unknown'));
      if (mappedError.terminal) {
        removeSensitiveAuthActionParams();
      }
      setState({
        phase: mappedError.terminal ? 'terminal-error' : 'recoverable-error',
        message: mappedError.message,
        targetEmail,
        maskedEmail,
      });
    } finally {
      submitLockRef.current = false;
    }
  };

  const preserveIntentForLogin = useCallback((): string | null => {
    const intentPath = resolveIntentPath();
    if (intentPath) {
      savePostAuthIntent(intentPath, 'auth-action-login');
    }
    return intentPath;
  }, [resolveIntentPath]);

  const handleGoToLogin = useCallback(() => {
    navigate(buildLoginTarget(preserveIntentForLogin()));
  }, [navigate, preserveIntentForLogin]);

  const handleDifferentAccount = useCallback(async () => {
    if (isLeaving) {
      return;
    }
    setIsLeaving(true);
    try {
      const intentPath = preserveIntentForLogin();
      await authService.logout();
      navigate(buildLoginTarget(intentPath), { replace: true });
    } finally {
      setIsLeaving(false);
    }
  }, [isLeaving, navigate, preserveIntentForLogin]);

  const handleContinue = useCallback(() => {
    if (state.phase !== 'success' || !state.nextPath) {
      return;
    }
    consumePostAuthIntent();
    navigate(state.nextPath);
  }, [navigate, state]);

  const handleStatusReadback = useCallback(() => {
    const intentPath = resolveIntentPath();
    if (intentPath) {
      savePostAuthIntent(intentPath, 'auth-action-status-readback');
    }
    const encodedIntent = encodePostAuthIntentForQuery(intentPath);
    navigate(encodedIntent ? `/verify-email?postAuthIntent=${encodedIntent}` : '/verify-email');
  }, [navigate, resolveIntentPath]);

  if (state.phase === 'parsing' || state.phase === 'checking') {
    return (
      <AuthActionShell
        heading="Sprawdzamy link weryfikacyjny"
        description="Potwierdzamy typ i ważność linku, zanim pozwolimy aktywować adres."
        statusProps={{ 'aria-live': 'polite' }}
      >
        <div className="flex min-h-28 items-center gap-3 text-sm text-primary-light-300">
          <Spinner size="sm" />
          Sprawdzanie linku...
        </div>
      </AuthActionShell>
    );
  }

  if (state.phase === 'ready') {
    return (
      <AuthActionShell
        headingRef={stateHeadingRef}
        heading="Potwierdź adres e-mail"
        description="Jedno kliknięcie aktywuje adres powiązany z tym linkiem."
      >
        <div className="rounded-lg bg-primary-dark-100/45 px-4 py-3">
          <p className="text-xs uppercase tracking-wide text-primary-light-400">Adres docelowy</p>
          <p className="mt-1 break-words font-semibold text-primary-light-100">
            {state.maskedEmail}
          </p>
        </div>
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          className="mt-6"
          onClick={handleApply}
        >
          Potwierdź adres e-mail
        </Button>
        <Link
          to="/login"
          className="mt-4 flex min-h-11 items-center justify-center rounded-lg text-sm text-ratuj-300 hover:text-ratuj-200 focus-outline"
        >
          Wróć do logowania
        </Link>
        <p className="mt-6 text-xs leading-5 text-primary-light-400">
          Adres jest zamaskowany dla prywatności. Kod nie zostanie użyty, dopóki nie wybierzesz
          głównej akcji.
        </p>
      </AuthActionShell>
    );
  }

  if (state.phase === 'applying' || state.phase === 'synchronizing') {
    const isApplying = state.phase === 'applying';
    return (
      <AuthActionShell
        heading={isApplying ? 'Potwierdzamy adres...' : 'Sprawdzamy status konta'}
        description={
          isApplying
            ? 'Firebase przetwarza link. Nie zamykaj jeszcze tej karty.'
            : 'Adres jest potwierdzony. Uzgadniamy teraz status konta w aplikacji.'
        }
        statusProps={{ 'aria-live': 'polite' }}
      >
        <div className="rounded-lg bg-primary-dark-100/45 px-4 py-3">
          <p className="text-sm font-semibold text-primary-light-100">{state.maskedEmail}</p>
        </div>
        <Button type="button" variant="primary" size="lg" fullWidth className="mt-6" disabled>
          <Spinner size="sm" color="white" className="mr-2" />
          {isApplying ? 'Potwierdzamy adres...' : 'Synchronizujemy status...'}
        </Button>
      </AuthActionShell>
    );
  }

  if (state.phase === 'success') {
    const isPartial = state.profileSync === 'unconfirmed';
    const currentAccountExit = resolveDefaultAppLanding(userProfile, claims);
    return (
      <AuthActionShell
        headingRef={stateHeadingRef}
        heading="Adres e-mail został potwierdzony"
        description={`Potwierdzony adres: ${state.maskedEmail}`}
        statusProps={{ 'aria-live': 'polite' }}
      >
        <Alert
          variant={isPartial || state.accountContext === 'different' ? 'warning' : 'success'}
          icon={
            isPartial || state.accountContext === 'different' ? (
              <InformationCircleIcon className="h-5 w-5" />
            ) : (
              <CheckCircleIcon className="h-5 w-5" />
            )
          }
        >
          {state.warning ||
            'Firebase i status konta w aplikacji potwierdzają zakończenie weryfikacji.'}
        </Alert>

        {state.accountContext === 'matching' && state.profileSync === 'confirmed' && (
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            className="mt-6"
            onClick={handleContinue}
          >
            Przejdź dalej
          </Button>
        )}
        {state.accountContext === 'matching' && state.profileSync === 'unconfirmed' && (
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            className="mt-6"
            onClick={handleStatusReadback}
          >
            Sprawdź status konta
          </Button>
        )}
        {state.accountContext === 'signed-out' && (
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            className="mt-6"
            onClick={handleGoToLogin}
          >
            Przejdź do logowania
          </Button>
        )}
        {state.accountContext === 'different' && (
          <>
            <Button
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              className="mt-6"
              disabled={isLeaving}
              onClick={handleDifferentAccount}
            >
              {isLeaving ? 'Wylogowywanie...' : 'Zaloguj się innym kontem'}
            </Button>
            <Link
              to={currentAccountExit}
              className="mt-4 flex min-h-11 items-center justify-center rounded-lg text-sm text-primary-light-300 hover:text-primary-light-100 focus-outline"
            >
              Pozostań na obecnym koncie
            </Link>
          </>
        )}

        <p className="mt-6 text-xs leading-5 text-primary-light-400">
          Wrażliwy kod został usunięty z widocznego adresu strony.
        </p>
      </AuthActionShell>
    );
  }

  if (state.phase === 'recoverable-error') {
    return (
      <AuthActionShell
        headingRef={stateHeadingRef}
        heading="Nie udało się sprawdzić linku"
        description="Nie wykonaliśmy potwierdzenia. Możesz bezpiecznie ponowić sprawdzenie."
      >
        <div role="alert">
          <Alert variant="error" icon={<ExclamationCircleIcon className="h-5 w-5" />}>
            {state.message}
          </Alert>
        </div>
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          className="mt-6"
          onClick={checkCode}
        >
          Spróbuj ponownie
        </Button>
        <Link
          to="/login"
          className="mt-4 flex min-h-11 items-center justify-center rounded-lg text-sm text-ratuj-300 hover:text-ratuj-200 focus-outline"
        >
          Wróć do logowania
        </Link>
      </AuthActionShell>
    );
  }

  const matchingUnverifiedAccount =
    Boolean(state.targetEmail) &&
    normalizeEmail(auth.currentUser?.email) === normalizeEmail(state.targetEmail) &&
    auth.currentUser?.emailVerified !== true;

  return (
    <AuthActionShell
      headingRef={stateHeadingRef}
      heading="Nie można potwierdzić adresu"
      description="Adres nie został zmieniony przez ten ekran."
    >
      <div role="alert">
        <Alert variant="error" icon={<ExclamationCircleIcon className="h-5 w-5" />}>
          {state.message}
        </Alert>
      </div>
      {matchingUnverifiedAccount ? (
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          className="mt-6"
          onClick={() => navigate('/verify-email')}
        >
          Wyślij nowy link
        </Button>
      ) : (
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          className="mt-6"
          onClick={handleGoToLogin}
        >
          Wróć do logowania
        </Button>
      )}
    </AuthActionShell>
  );
};

interface UnsupportedAuthActionProps {
  title: string;
  description: string;
  showResetLink?: boolean;
}

const UnsupportedAuthAction: React.FC<UnsupportedAuthActionProps> = ({
  title,
  description,
  showResetLink = false,
}) => {
  return (
    <AuthActionShell heading={title} description={description} statusProps={{ role: 'alert' }}>
      <Link
        to="/login"
        className="flex min-h-[46px] w-full items-center justify-center rounded-lg bg-ratuj-400 px-5 py-2.5 text-base font-semibold text-white shadow-button transition-colors hover:bg-ratuj-300 focus-outline"
      >
        Wróć do logowania
      </Link>
      {showResetLink && (
        <Link
          to="/reset-password"
          className="mt-4 flex min-h-11 items-center justify-center rounded-lg text-sm text-ratuj-300 hover:text-ratuj-200 focus-outline"
        >
          Przejdź do resetu hasła
        </Link>
      )}
    </AuthActionShell>
  );
};

const AuthActionPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [action] = useState<ParsedAuthAction>(() => parseAuthActionQuery(searchParams));

  useEffect(() => {
    if (action.kind === 'malformed' || action.kind === 'unsupported') {
      removeSensitiveAuthActionParams();
    }
  }, [action.kind]);

  if (action.kind === 'resetPassword') {
    return (
      <ResetPasswordAction
        oobCode={action.oobCode}
        intentPath={action.continueIntent}
        onTerminalResult={removeSensitiveAuthActionParams}
      />
    );
  }

  if (action.kind === 'verifyEmail') {
    return <VerifyEmailAction oobCode={action.oobCode} continueIntent={action.continueIntent} />;
  }

  if (action.kind === 'unsupported') {
    return (
      <UnsupportedAuthAction
        title="Nieobsługiwana akcja konta"
        description={
          action.mode === 'recoverEmail'
            ? 'Przywracanie poprzedniego adresu e-mail nie jest obsługiwane w tej aplikacji.'
            : 'Ten typ linku Firebase nie jest obsługiwany w tej aplikacji.'
        }
        showResetLink
      />
    );
  }

  return (
    <UnsupportedAuthAction
      title={action.reason === 'missing-mode' ? 'Brak typu akcji' : 'Link nie jest kompletny'}
      description={
        action.reason === 'missing-mode'
          ? 'Link nie określa, jaką akcję konta należy wykonać.'
          : 'Link nie zawiera kodu potrzebnego do bezpiecznego wykonania akcji.'
      }
      showResetLink
    />
  );
};

export default AuthActionPage;
